import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IAccount,
  IAccountBlockingReason,
  IAccountBlockingReasonRole,
  IAccountBlockingType,
  IBlockAccountParams,
  IBlockingReasonRole,
} from '@app/shared/models/banking.model';
import { BlockReasonParam, BlockReasonTypeParam } from '@app/shared/models/param.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { UserContext } from '@app/shared/services/context/user.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { BLOCK_REASON_TYPE, CONTRACT_TYPE, COUNTRY, PRODUCT_TYPE, STATUS } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { Scopes } from '@app/shared/types/enums/scopes';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, concatMap, EMPTY, filter, map, Observable, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';

@Component({
  selector: 'app-block-unlock-acc',
  templateUrl: './block-unlock-acc.component.html',
  styleUrls: ['./block-unlock-acc.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class BlockUnlockAccComponent extends SubscriberComponent implements OnInit, OnDestroy {
  public blockAccountForm!: FormGroup;

  public isLoading = false;
  public reasons!: IAccountBlockingReason[];
  public reasonsRole!: IAccountBlockingReasonRole[];
  public roles!: IBlockingReasonRole[];
  public types!: IAccountBlockingType[];
  account!: IAccount;
  STATUS = STATUS;
  rppUserId!: string;
  initialState!: IDynamicDialogConfig;
  selectReason: boolean = false;
  readonly TOTAL_UNLOCK = 'TOTAL_UNLOCK';
  readonly TOTAL_BLOCK = 'TOTAL_BLOCK';
  readonly UNLOCK = 'unlock';
  readonly BLOCK = 'block';
  private closeModal$ = new Subject<void>();
  question!: string;
  statement!: string;
  selectLabel!: string;
  successMessage!: string;

  constructor(
    private readonly _accBscSvc: AccountBasicService,
    private readonly _accountCtx: AccountContext,
    private readonly modalSvc: ModalService,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService,
    public readonly _contractContextService: ContractContext,
    public readonly _userCtxSvc: UserContext
  ) {
    super();
  }

  ngOnInit() {
    this.getmodalData();
    this.logAmplitudeEvent();
    this.getAccount();
    this.createForm();
    this.confirmActionSubscription();
    this.setTitles();
    this.setupModalCloseSubscription();
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this.modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this.modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private confirmActionSubscription(): void {
    this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => this.getBlockAccountParams(agentData)),
        switchMap(params => this.executeBlockAccount(params)),
        tap(() => this.handleBlockAccountSuccess()),
        catchError(() => {
          this.handleBlockAccountError();

          return EMPTY;
        })
      )
      .subscribe();
  }

  private getBlockAccountParams(agentData: any): Observable<IBlockAccountParams> {
    const agent_email = agentData?.email || '';
    const selectedReasonValue = this.blockAccountForm.get('reason')?.value?.toString() || '';
    const selectedRole = this.roles.find(res => res.block_reason_id?.toString() === selectedReasonValue);
    const areaIdFromResponse = this.reasonsRole?.[0]?.areaId;

    const params: IBlockAccountParams = {
      rppUserId: this._customerCtxSvc.getRppUserId(),
      accountCode: this._accountCtx.getCurrentAccount()?.code || '',
      reasonId: selectedReasonValue,
      reasonDescription: selectedRole?.name,
      typeId: this.blockAccountForm.get('type')?.value.toString() || '',
      blockType: this.account.status === STATUS.ENABLE ? this.TOTAL_BLOCK : this.TOTAL_UNLOCK,
      agent_email,
      areaId: areaIdFromResponse,
      action: this.initialState.action,
    };

    return of(params);
  }

  private executeBlockAccount(params: IBlockAccountParams): Observable<any> {
    return this._accBscSvc.blockAccount(params);
  }

  private handleBlockAccountSuccess(): void {
    this.modalSvc.closeWithSuccessMsg(this.initialState.modalComponentData.modalName);
    this.rppUserId = this._customerCtxSvc.getRppUserId();

    this._subscriptions.sink = timer(2000) // wait for 2 seconds to make sure the contract is blocked/unblocked
      .pipe(
        takeUntil(this.closeModal$),
        concatMap(() => this._accBscSvc.getAccounts(this.rppUserId)),
        concatMap(accounts => {
          if (!accounts) {
            return of(null);
          }
          const selectedAccount = accounts.find(account => account.code === this.account?.code);
          this._accountCtx.accountChange(selectedAccount);
          this._accountCtx.accountCodeChange(selectedAccount?.code);

          return of(accounts);
        })
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  private handleBlockAccountError(): void {
    this.modalSvc.closeWithErrorMsg(this.initialState.modalComponentData.modalName);
  }

  private logAmplitudeEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.LOCK_ACCOUNT,
    });
  }

  getAccount() {
    this._subscriptions.sink = this._accountCtx.accountChanged$.subscribe({
      next: account => {
        this.account = account as IAccount;
      },
    });
    this.getReasons(this.account.status);
  }

  createForm() {
    this.blockAccountForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  getReasons(accountStatus: string) {
    switch (accountStatus) {
      case STATUS.ENABLE:
        accountStatus = this.BLOCK;
        break;
      case STATUS.BLOCKED:
        accountStatus = this.UNLOCK;
        break;
      default:
        accountStatus = this.BLOCK;
        break;
    }
    if (this._customerCtxSvc.context?.country === COUNTRY.CO) {
      const blockParams: BlockReasonTypeParam = {
        scope: this._userCtxSvc.getRoles()[0].toLowerCase(),
        accountCode: this.account.code,
        action: this.initialState.action || '',
      };
      this._accBscSvc.getBlockingReasonsRole(blockParams).subscribe({
        next: async reasons => {
          this.reasonsRole = reasons.data ? [reasons.data] : [];
          this.roles = this.reasonsRole.find(() => true)?.reasons || [];
        },
      });
    } else {
      this._subscriptions.sink = this._customerCtxSvc.contextChanged$.subscribe({
        next: user => {
          this._accBscSvc.getBlockingReasons(user?.rppUserId || '', accountStatus).subscribe({
            next: reasons => {
              this.reasons = reasons.data || [];
            },
          });
        },
      });
    }
  }

  onChangesForm() {
    this._subscriptions.sink = this.blockAccountForm.valueChanges.subscribe(value => {
      if (this.blockAccountForm.valid) {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: false,
            formData: value,
          },
        };

        this.modalSvc.setModalData(data);
      } else {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: true,
            formData: value,
          },
        };

        this.modalSvc.setModalData(data);
      }
    });
  }

  getTypeByReason(reason: string) {
    const types = this.roles.filter(x => x.block_reason_id === reason);
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => (this.initialState = data),
    });
  }

  scopeAssign() {
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK)) {
      return Scopes.A_CONTRACT_BLOCK;
    }
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK_PB)) {
      return Scopes.A_CONTRACT_BLOCK_PB;
    }
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK_FRAUD)) {
      return Scopes.A_CONTRACT_BLOCK_FRAUD;
    }
    return '';
  }

  setTitles() {
    switch (this.initialState.action) {
      case 'block':
        this.question = 'MODAL_BLOCK_ACC.ALERT';
        this.statement = 'MODAL_BLOCK_ACC.STATEMENT';
        this.selectLabel = 'MODAL_BLOCK_ACC.SELECT';
        this.successMessage = 'ACCOUNT.MODAL.BLOCK.INFO_MESSAGE_SUCCESS';
        break;
      case 'unblock':
        this.question = 'MODAL_UNBLOCK_ACC.ALERT';
        this.statement = 'MODAL_UNBLOCK_ACC.STATEMENT';
        this.selectLabel = 'MODAL_UNBLOCK_ACC.SELECT';
        this.successMessage = 'ACCOUNT.MODAL.UNBLOCK.INFO_MESSAGE_SUCCESS';
        break;
      default:
        this.question = '';
        this.statement = '';
        this.selectLabel = '';
        this.successMessage = '';
        break;
    }
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.unsubscribe();
  }
}
